import { format, isValid, subDays } from 'date-fns';
import * as yup from 'yup';
import {
  COUNTRY_STATES,
  NOT_APPLICABLE_STATE_CODE,
  REGEX_EMAIL,
  SupportedCountryCode,
} from './constants';
import { parseDateString } from './date';

type TLengthConstraints = {
  min: number;
  max: number;
};

type TValidationConstraints = TLengthConstraints & {
  dataType: 'number' | 'string';
};

type TRequiredMessage = { requiredMessage?: string };

const defaultLengthConstraints = { min: 1, max: Number.MAX_VALUE };

// Field validation error messages
export const FIELD_VALIDATION_REQUIRED = 'Required';
export const FIELD_VALIDATION_INVALID_POST_CODE =
  'Invalid Postcode, only numbers, letters and spaces are allowed.';
export const FIELD_VALIDATION_INVALID_NAME = 'Please enter a valid name';
export const FIELD_VALIDATION_INVALID_EMAIL = 'Pleas enter a valid email';
// UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
export const FIELD_VALIDATION_PHOTO_CARD_NUMBER_UNREADABLE =
  'Enter the number as printed on the card. It cannot contain a question mark.';

const REGEX_FULL_NAME = /^[\p{Letter}\p{Mark}']+(((\s|-)[\p{Letter}\p{Mark}']+)+)?$/u;
const REGEX_ONLY_LETTERS = /^[a-zA-Z]+$/;

export const REQUIRED_EMAIL_RULES = requiredStringRule().matches(
  REGEX_EMAIL,
  FIELD_VALIDATION_INVALID_EMAIL,
);

export const REQUIRED_URL_RULES = requiredStringRule().test(
  'is-valid-url',
  'Please enter a valid http:// or https:// URL',
  (value) => {
    try {
      const url = new URL(value);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  },
);

export const OPTIONAL_URL_RULES = optionalRule(REQUIRED_URL_RULES);

export const OPTIONAL_STRING_RULES = yup.string().notRequired();
export const REQUIRED_STRING_RULES = requiredStringRule();

export const OPTIONAL_ARRAY_RULES = yup.array().notRequired();
export const REQUIRED_ARRAY_RULES = yup.array().min(1, 'At least one value is required');

export const OPTIONAL_BOOLEAN_RULES = yup.bool().notRequired();
export const REQUIRED_BOOLEAN_RULES = yup.bool().oneOf([true], 'Required').required();
export const REQUIRED_PHONE_NUMBER_RULES = requiredStringRule({
  requiredMessage: 'Please enter a valid phone number',
}).matches(
  /^\+[1-9]\d{0,3}(\s|-)?[\d\s\-()]{4,14}$/,
  'Please enter a valid phone number, e.g. +61 412345678',
);
export const OPTIONAL_PHONE_NUMBER_RULES = optionalRule(REQUIRED_PHONE_NUMBER_RULES);
export const REQUIRED_POST_CODE_VALIDATION_RULES = requiredStringRule().matches(
  /^[0-9A-Za-z ]+$/,
  FIELD_VALIDATION_INVALID_POST_CODE,
);

export const REQUIRED_SINGLE_DIGIT_1_TO_9_VALIDATION_RULES = requiredStringRule().matches(
  /^[1-9]$/,
  'Only single digit allowed, please enter a number between 1 and 9',
);

export const REQUIRED_FULL_NAME_VALIDATION_RULES = requiredStringRule().matches(
  REGEX_FULL_NAME,
  FIELD_VALIDATION_INVALID_NAME,
);

export const OPTIONAL_FULL_NAME_VALIDATION_RULES = optionalRule(
  requiredStringRule().matches(REGEX_FULL_NAME, FIELD_VALIDATION_INVALID_NAME),
);

export const REQUIRED_ONLY_LETTERS_VALIDATION_RULES = requiredStringRule().matches(
  REGEX_ONLY_LETTERS,
  'Only letters allowed',
);

export const REQUIRED_DRIVER_LICENCE_NUMBER_VALIDATION_RULES = requiredStringRule({
  min: 6,
  max: 16,
});

// Regula marks a character it cannot read with `?` (verifime-adr#998). A photo card has no DVS
// check, so nothing downstream catches the marker; the customer corrects it here. The driver
// licence rule above stays as it is: DSS needs the marker on the licence path.
const UNREADABLE_MARKER = '?';

export const REQUIRED_PHOTO_CARD_NUMBER_VALIDATION_RULES =
  REQUIRED_DRIVER_LICENCE_NUMBER_VALIDATION_RULES.test(
    'has-no-unreadable-marker',
    FIELD_VALIDATION_PHOTO_CARD_NUMBER_UNREADABLE,
    (value) => !value || !value.includes(UNREADABLE_MARKER),
  );

export const REQUIRED_PASSPORT_VALIDATION_RULES = requiredStringRule({ min: 5, max: 15 });

// An ImmiCard number is three letters then six digits (for example AMS015170). The customer may
// type it in lower case or with spaces, so it is upper-cased and stripped of spaces before it is
// checked against ^[A-Z]{3}[0-9]{6}$ and before it is sent.
const REGEX_IMMI_CARD_NUMBER = /^[A-Z]{3}[0-9]{6}$/;

export function normaliseImmiCardNumber(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase();
}

export const REQUIRED_IMMI_CARD_NUMBER_VALIDATION_RULES = yup
  .string()
  .required(FIELD_VALIDATION_REQUIRED)
  .test(
    'is-immi-card-number',
    // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
    'Please enter a valid ImmiCard number, e.g. AMS015170',
    (value) => !value || REGEX_IMMI_CARD_NUMBER.test(normaliseImmiCardNumber(value)),
  );

export const REQUIRED_EXPIRY_DATE_VALIDATION_RULES = requiredDateRule();

// For a document that can print no expiry (two photo card templates print none). An empty value
// passes; anything the customer has typed must still be a real date.
export const OPTIONAL_EXPIRY_DATE_VALIDATION_RULES = optionalDateRule();

export const MIN_BIRTH_OF_DATE_GAP = 365 * 120; //  120 years old
export const REQUIRED_DATE_OF_BIRTH_VALIDATION_RULES = requiredDateRule({
  min: subDays(new Date(), MIN_BIRTH_OF_DATE_GAP + 1),
  max: new Date(),
});

export const REQUIRED_DRIVER_LICENCE_CARD_NUMBER_VALIDATION_RULES = requiredStringRule({
  min: 1,
  max: 10,
});

// The same rule family as the licence number and the card number above, so every mandatory
// field on the driver licence form reports itself the same way: `Required` when empty and the
// length messages when it is the wrong size. VME-4061: the form is not to carry a second
// validation vocabulary.
//
// This deliberately does not check that the value is numeric, which requiredNumberRule would
// have added along with its own `Must be only digits` wording. person-api declares
// DriverLicence.licenceVersionNumber with dataType "Integer", so a letter typed here is refused
// by the backend as a conversion error rather than by the field. See TD-028 in
// docs/technical-debt.md.
export const REQUIRED_DRIVER_LICENCE_VERSION_NUMBER_VALIDATION_RULES = requiredStringRule({
  min: 1,
  max: 10,
});

function optionalRule(basicRule: yup.Schema): any {
  return yup.lazy<yup.Schema>((value) => {
    if (!!value) {
      return basicRule;
    }
    return yup.mixed().notRequired();
  });
}

export const fileRule = (requiredErrorMessage: string) => {
  return yup.object().shape({
    file: yup
      .object()
      .required(requiredErrorMessage)
      .test('fileSize', 'The maximum file size allowed is 10MB', (value: any) => {
        const { file }: { file: File } = value;
        // Maximum allowed file size is 10MB
        if (file?.size > 10 * 1000 * 1000) {
          return false;
        }
        return true;
      })
      .test('fileType', 'Please choose only pdf, jpeg or png file', (value: any) => {
        const { file }: { file: File } = value;
        if (['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file?.type)) {
          return true;
        }
        return false;
      }),
  });
};

export const optionalFileRule: any = (requiredErrorMessage: string) => {
  return optionalRule(fileRule(requiredErrorMessage));
};

/**
 *
 * If need to check exactly same length, just make min equals max, namely `min === max`
 *
 * @param options
 * `min`: the minimum length of number, default is `1`
 *
 * `max`: the maximum length of number, default is `Number.MAX_VALUE`
 */
function requiredRule({
  min,
  max,
  dataType,
  requiredMessage = 'Required',
}: TValidationConstraints & TRequiredMessage) {
  let basicRule = yup.string().trim().required(requiredMessage);

  let showType = 'characters';

  if (dataType === 'number') {
    showType = 'digits';
    basicRule = basicRule.matches(/^[0-9]+$/, `Must be only ${showType}`);
  }

  let minMessage = `Must be at least ${min} ${showType}`;
  let maxMessage = `Must be at most ${max} ${showType}`;

  if (min === max) {
    minMessage = `Must be exactly ${min} ${showType}`;
    maxMessage = `Must be exactly ${max} ${showType}`;
  }
  return basicRule.min(min, minMessage).max(max, maxMessage);
}

export function requiredNumberRule({
  min = defaultLengthConstraints.min,
  max = defaultLengthConstraints.max,
}: TLengthConstraints = defaultLengthConstraints) {
  return requiredRule({ min, max, dataType: 'number' });
}

export function optionalNumberRule({
  min = defaultLengthConstraints.min,
  max = defaultLengthConstraints.max,
}: TLengthConstraints = defaultLengthConstraints) {
  return optionalRule(requiredNumberRule({ min, max }));
}

export function requiredStringRule({
  min = defaultLengthConstraints.min,
  max = defaultLengthConstraints.max,
  requiredMessage,
}: Partial<TLengthConstraints> & TRequiredMessage = defaultLengthConstraints) {
  return requiredRule({ min, max, dataType: 'string', requiredMessage });
}

export function optionalStringRule({
  min = defaultLengthConstraints.min,
  max = defaultLengthConstraints.max,
}: TLengthConstraints = defaultLengthConstraints) {
  return optionalRule(requiredStringRule({ min, max }));
}

export function requiredDateRule({ min, max }: { min?: Date; max?: Date } = {}) {
  let basicRule = yup
    .date()
    .transform(parseDateString)
    .typeError('Please enter a valid date')
    .required(FIELD_VALIDATION_REQUIRED);

  if (min && isValid(min)) {
    basicRule = basicRule.min(min, `Min date should be ${format(min, 'dd/MM/yyyy')}`);
  }
  if (max && isValid(max)) {
    basicRule = basicRule.max(max, `Max date should be ${format(max, 'dd/MM/yyyy')}`);
  }
  return basicRule;
}

export function optionalDateRule({ min, max }: { min?: Date; max?: Date } = {}) {
  return optionalRule(requiredDateRule({ min, max }));
}

export function nonApplicableTextInputRule(message?: string) {
  return yup.string().max(0, message ?? 'Not applicable');
}

export function countryStateSelectInputRule(countryCode: SupportedCountryCode) {
  if (!Object.keys(COUNTRY_STATES).includes(countryCode)) {
    return nonApplicableCountryStateSelectInputRule();
  }

  type TCountryCodeWithState = keyof typeof COUNTRY_STATES;
  const stateCodes = COUNTRY_STATES[countryCode as TCountryCodeWithState].map(
    (option) => option.code,
  );
  // The customer picks the state; an empty selection is the only reachable
  // failure, so both tests report it the same way every other field does.
  return yup
    .string()
    .oneOf(stateCodes, FIELD_VALIDATION_REQUIRED)
    .required(FIELD_VALIDATION_REQUIRED);
}

function nonApplicableCountryStateSelectInputRule() {
  // Reports itself the same way every other field on the form does. The country-specific wording
  // this replaced ("State is not applicable for New Zealand") was the only sentence of its kind on
  // the driver licence form, and it reached the screen as a leftover from the country the customer
  // had just switched away from - naming a country they were no longer on. VME-4061: keep it
  // simple, one word, the same word.
  return yup.string().oneOf([NOT_APPLICABLE_STATE_CODE], FIELD_VALIDATION_REQUIRED);
}
